import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Leaf, Phone, LayoutGrid, Sparkles } from 'lucide-react';
import HomePage from '@/components/pages/HomePage';
import GrundrisseView from '@/components/pages/GrundrisseView';
import NachhaltigkeitView from '@/components/pages/NachhaltigkeitView';
import KontaktView from '@/components/pages/KontaktView';
import ImpressumView from '@/components/pages/ImpressumView';
import DatenschutzView from '@/components/pages/DatenschutzView';
import RechtlicheHinweiseView from '@/components/pages/RechtlicheHinweiseView';
import Footer from '@/components/Footer';

const AppLayout = () => {
  const [currentView, setCurrentView] = useState('home');

  // Primary navigation (sidebar + bottom nav)
  const navViews = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'grundrisse', label: 'Grundrisse', icon: LayoutGrid },
    { id: 'nachhaltigkeit', label: 'Nachhaltigkeit', icon: Leaf },
    { id: 'kontakt', label: 'Kontakt', icon: Phone }
  ];

  // All views incl. legal pages (reachable via footer)
  const components = {
    home: HomePage,
    grundrisse: GrundrisseView,
    nachhaltigkeit: NachhaltigkeitView,
    kontakt: KontaktView,
    impressum: ImpressumView,
    datenschutz: DatenschutzView,
    rechtliches: RechtlicheHinweiseView
  };

  const navigate = (id) => {
    setCurrentView(id);
    // Scroll content back to top on view change
    const c = document.querySelector('[data-scroll-container]');
    if (c) c.scrollTo({ top: 0, behavior: 'auto' });
  };

  const CurrentComponent = components[currentView];

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#0d1a0d]">
      {/* ===== DESKTOP: Left Sidebar (lg and up) ===== */}
      <nav
        data-testid="main-navigation"
        className="hidden lg:flex fixed left-0 top-0 h-full w-[260px] bg-gradient-to-b from-[#1a2e1a] via-[#0f1a0f] to-[#0d1a0d] shadow-2xl z-40 flex-col border-r border-[#D4AF37]/20"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

        <div className="p-8 border-b border-[#D4AF37]/20 relative">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
            <div>
              <h1 className="text-2xl xl:text-3xl font-light text-white tracking-wide leading-tight" style={{ fontFamily: "'Cormorant', serif" }}>
                Green Living
              </h1>
              <p className="text-xs text-[#F4E4C1]/60 font-light tracking-wide mt-1">Leben am Wasser</p>
            </div>
          </div>
        </div>

        <div className="flex-1 py-8 px-4 space-y-3">
          {navViews.map((view) => {
            const Icon = view.icon;
            const isActive = currentView === view.id;
            return (
              <button
                key={view.id}
                data-testid={`nav-${view.id}`}
                onClick={() => navigate(view.id)}
                className={`w-full flex items-center gap-4 px-6 py-4 rounded-xl transition-all duration-500 relative overflow-hidden ${
                  isActive ? 'text-white shadow-lg' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeTabDesktop"
                    className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/20 to-[#C9A961]/10 border border-[#D4AF37]/30 rounded-xl"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <Icon className={`w-5 h-5 relative z-10 flex-shrink-0 ${isActive ? 'text-[#D4AF37]' : ''}`} />
                <span className="text-base font-light tracking-wide relative z-10">{view.label}</span>
                {isActive && <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] ml-auto relative z-10" />}
              </button>
            );
          })}
        </div>

        <div className="p-6 border-t border-[#D4AF37]/20 relative">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <p className="text-xs text-[#F4E4C1]/40 font-light leading-relaxed">
            Berlin-Schmöckwitz<br />
            ProDomo Projektentwicklung
          </p>
          <div className="flex items-center gap-2 mt-3">
            <div className="w-1 h-1 rounded-full bg-[#D4AF37]" />
            <span className="text-[10px] text-[#D4AF37]/60 uppercase tracking-widest">Am Wasser</span>
          </div>
        </div>
      </nav>

      {/* ===== MOBILE: Top Bar ===== */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-gradient-to-r from-[#1a2e1a] to-[#0d1a0d] border-b border-[#D4AF37]/20 z-40 flex items-center px-5">
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
        <Sparkles className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
        <div className="ml-3">
          <h1 className="text-xl font-light text-white tracking-wide leading-none" style={{ fontFamily: "'Cormorant', serif" }}>
            Green Living
          </h1>
          <p className="text-[10px] text-[#F4E4C1]/60 font-light tracking-wide">Leben am Wasser</p>
        </div>
      </header>

      {/* ===== MOBILE: Bottom Navigation ===== */}
      <nav
        data-testid="mobile-navigation"
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#0d1a0d] to-[#1a2e1a] border-t border-[#D4AF37]/20 z-40 flex items-stretch"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
        {navViews.map((view) => {
          const Icon = view.icon;
          const isActive = currentView === view.id;
          return (
            <button
              key={view.id}
              data-testid={`mobilenav-${view.id}`}
              onClick={() => navigate(view.id)}
              className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 px-1 transition-all duration-300 relative ${
                isActive ? 'text-[#D4AF37]' : 'text-white/50'
              }`}
            >
              {isActive && (
                <motion.div
                  layoutId="activeTabMobile"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-[#D4AF37] rounded-full"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="text-[10px] font-light tracking-wide text-center leading-tight">{view.label}</span>
            </button>
          );
        })}
      </nav>

      {/* ===== Main Content Area (scroll container holds view + footer) ===== */}
      <div
        data-scroll-container
        className="absolute inset-0 lg:left-[260px] top-16 lg:top-0 bottom-[72px] lg:bottom-0 overflow-y-auto"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="min-h-full"
          >
            {CurrentComponent && <CurrentComponent onNavigate={navigate} />}
          </motion.div>
        </AnimatePresence>

        {/* Footer with legal links - present on every page */}
        <Footer onNavigate={navigate} />
      </div>

      {/* Grain Texture Overlay */}
      <div className="grain-texture fixed inset-0 pointer-events-none z-30" />
    </div>
  );
};

export default AppLayout;
